// CANONICAL COPY (GH #7115). src/state/evaluateControl.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

// 6-gate compliance evaluation engine (Gate 6 is opt-in via ownershipSettings.gateEnabled)
const { fmtDate } = require('./formatDate.cjs');
const { resolveReaders, unresolvedReaderTargets, hasReaderAcknowledged } = require('./resolveReaders.cjs');
const { LEGACY_TO_SCF } = require('./legacyToScf.cjs');
const { OBLIGATION_CATALOG } = require('./obligations/index.cjs');
const { enabledFrameworkIdSet } = require('./frameworkEnabled.cjs');
const { countsAsEvidence } = require('./artefactEvidence.cjs');
const { controlWorkflowEvidence } = require('../workflows/workflowEvidence.cjs');
const { strengthRank } = require('../workflows/actionCatalogue.cjs');

/**
 * Normalise a control id to its TCF canonical form. TCF ids pass through
 * unchanged; legacy native ids (A.5.1, CIS-1.1, …) are migrated. Anything
 * not in the legacy table is returned as-is so unrelated identifiers are
 * still self-equal.
 */
function canonicalControlId(id) {
  return LEGACY_TO_SCF[id] || id;
}

/**
 * Normalise a document's controls array to an array of controlId strings.
 * Handles both legacy string format ["A.5.1"] and new object format
 * [{ controlId: "A.5.1", blockId: "blk_xxx", selectedText: "..." }]
 */
function docControlIds(doc) {
  return (doc.controls || []).map((c) => (typeof c === 'string' ? c : c.controlId));
}

/**
 * Does this document cover the given control? Tolerant of either side
 * being tagged with a native legacy id or a TCF canonical id — both are
 * normalised through LEGACY_TO_SCF before comparison so the list view
 * (which queries by TCF) and the modal (which historically queried by
 * native id) agree on which docs are linked.
 */
function docCoversControl(doc, controlId) {
  const target = canonicalControlId(controlId);
  return docControlIds(doc).some((cid) => canonicalControlId(cid) === target);
}

// Was this document ever published? Uses everPublished flag (new), with
// publishedVersion / publishedAt as fallbacks for legacy docs predating the flag.
function wasEverPublished(d) {
  return d.everPublished === true || !!d.publishedVersion || !!d.publishedAt;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * All control ids a risk is linked to: the legacy top-level controlIds/controls
 * arrays plus the controls picked on the selected treatment option (the
 * per-option model — TreatmentControlPickerModal). Entries may be plain id
 * strings or { frameworkId, controlId } objects; both are unwrapped. Mirrors
 * the dual-read in SoAPage's controlToRisks derivation. The live resolutions[]
 * lookup is preferred over the denormalised selectedResolution copy.
 */
function riskControlIds(risk) {
  const top = [...(risk.controlIds || []), ...(risk.controls || [])];
  const selRes =
    (risk.selectedResolutionId &&
      (risk.resolutions || []).find((o) => o.id === risk.selectedResolutionId)) ||
    risk.selectedResolution ||
    null;
  const fromOption = selRes?.controls || [];
  return [...top, ...fromOption]
    .map((c) => (typeof c === 'string' ? c : c?.controlId))
    .filter(Boolean);
}

/**
 * Is this risk obligation currently satisfied? True when it has been
 * satisfied at least once AND the satisfaction is still inside its
 * cadence + grace window (same window semantics as isDecisionValid /
 * the governance sweeper). Stale satisfaction past the window does not
 * count as evidence — OBL-B-002: when the evidence ages out, the
 * compliance signal goes with it.
 */
function isObligationSatisfied(ob, nowMs) {
  if (!ob || ob.active === false || !ob.lastSatisfiedAt) return false;
  const satisfiedMs = new Date(ob.lastSatisfiedAt).getTime();
  if (!Number.isFinite(satisfiedMs)) return false;
  if (Number.isFinite(ob.cadenceDays) && Number.isFinite(ob.graceDays)) {
    return nowMs <= satisfiedMs + (ob.cadenceDays + ob.graceDays) * DAY_MS;
  }
  return true;
}

/**
 * Evaluate a control's gates as of a point in time.
 *
 * @param asOf  Optional evaluation instant — ms epoch, Date, or ISO string.
 *              Defaults to the live `Date.now()`. This is the single seam the
 *              Audit-Readiness Forecast (src/state/forecastControl.js) uses to
 *              project gate status onto a future audit date: every time-anchored
 *              comparison below reads `asOfMs` instead of inlining `new Date()`,
 *              so `forecastControl(..., asOf = now)` is byte-for-byte identical
 *              to the live evaluation (parity property — AC-003).
 */
function evaluateControl(controlId, documents, domainContext, asOf) {
  // Normalise the evaluation instant. `null`/`undefined` → live now, preserving
  // the exact pre-existing behaviour for all existing callers (which pass no
  // 4th arg). A non-finite asOf also falls back to now so a bad input never
  // silently evaluates against epoch 0.
  let asOfMs =
    asOf == null ? Date.now() : typeof asOf === 'number' ? asOf : new Date(asOf).getTime();
  if (!Number.isFinite(asOfMs)) asOfMs = Date.now();
  // A due date is overdue when it falls strictly before the evaluation instant.
  // Mirrors isOverdue() in src/utils/formatting.js (new Date(due) < new Date())
  // with `now` parameterised so the forecast can ask "overdue as of date T?".
  const overdueAsOf = (due) => !!due && new Date(due).getTime() < asOfMs;
  const {
    obligations = [],
    artefacts = [],
    risks = [],
    employees = [],
    // state.documentAssignments — required while the legacy fold exists
    // (DOC-B-026, design §d.1); buildControlDomainContext supplies it.
    documentAssignments,
    documentTypes = [],
    controlOwnership = {},
    ownershipSettings = {},
    obligationDecisions = {},
    riskObligations = [],
    // PDWB-03 — workflowEvidenceOf(state): { status, definitions }. Absent for
    // a caller that has not wired it: then nothing is counted and the reason
    // says workflow evidence was not read (never "no workflows").
    workflowEvidence,
    enabledObligations = [],
    obligationsById,
    // Tenant framework list. Left `undefined` (not `[]`) when the caller
    // hasn't wired it — callers that don't pass it keep the pre-existing
    // unfiltered behaviour rather than having every framework-tied
    // obligation silently drop out.
    frameworks,
  } = domainContext || {};
  const docs = documents.filter((d) => docCoversControl(d, controlId));

  // GATE 1 — At least one currently-published document covers this control.
  // Distinguish three sub-states for the reason text:
  //   (a) currently published        → pass
  //   (b) previously published, now draft → fail with "regressed" wording
  //   (c) draft only, never published   → fail
  //   (d) no document at all          → fail
  const publishedDocs = docs.filter((d) => (d.documentStatus || 'draft') === 'published');
  const previouslyPublished = docs.filter(
    (d) => (d.documentStatus || 'draft') !== 'published' && wasEverPublished(d)
  );
  const hasDoc = docs.length > 0;
  const hasPublishedDoc = publishedDocs.length > 0;

  let gate1Reason;
  if (hasPublishedDoc) {
    gate1Reason = `${publishedDocs.length} published document${publishedDocs.length > 1 ? 's' : ''} mapped`;
  } else if (previouslyPublished.length > 0) {
    const titles = previouslyPublished.map((d) => `"${d.title}"`).join(', ');
    gate1Reason = `${titles} previously published but currently in draft — republish required`;
  } else if (hasDoc) {
    gate1Reason = `${docs.length} document${docs.length > 1 ? 's' : ''} mapped but never published`;
  } else {
    gate1Reason = 'No document describes this control';
  }

  const gate1 = {
    id: 'document',
    label: 'Document exists',
    pass: hasPublishedDoc,
    state: hasPublishedDoc ? 'pass' : 'fail',
    reason: gate1Reason,
    meta: {
      docs: docs.map((d) => ({
        id: d.id,
        title: d.title,
        documentStatus: d.documentStatus || 'draft',
        version: d.version,
        everPublished: wasEverPublished(d),
      })),
    },
  };

  // GATE 2 — Every published document reviewed within SLA.
  // Blocked when Gate 1 fails: there is nothing eligible for review yet.
  const reviewableDocs = publishedDocs;
  const overdueDoc = reviewableDocs.find((d) => d.nextReview && overdueAsOf(d.nextReview));
  // #7146: a published document with no nextReview has no review schedule,
  // so it can never show it was reviewed on time — it fails this gate.
  const undatedDoc = reviewableDocs.find((d) => !d.nextReview);
  let gate2State, gate2Reason;
  if (!hasPublishedDoc) {
    gate2State = 'blocked';
    gate2Reason = 'Blocked — no published document to review';
  } else if (overdueDoc) {
    gate2State = 'fail';
    gate2Reason = `"${overdueDoc.title}" review was due ${fmtDate(overdueDoc.nextReview)}`;
  } else if (undatedDoc) {
    gate2State = 'fail';
    gate2Reason = `"${undatedDoc.title}" has no review date set`;
  } else {
    gate2State = 'pass';
    gate2Reason = 'All published documents reviewed within SLA';
  }
  const gate2 = {
    id: 'review',
    label: 'Review within SLA',
    pass: gate2State === 'pass',
    state: gate2State,
    reason: gate2Reason,
    meta: {
      overdueDoc: overdueDoc
        ? { id: overdueDoc.id, title: overdueDoc.title, nextReview: overdueDoc.nextReview }
        : null,
      undatedDoc: undatedDoc ? { id: undatedDoc.id, title: undatedDoc.title } : null,
      docs: reviewableDocs.map((d) => ({
        id: d.id,
        title: d.title,
        nextReview: d.nextReview || null,
        overdue: d.nextReview ? overdueAsOf(d.nextReview) : false,
      })),
    },
  };

  // GATE 3 — All required readers acknowledged latest material version.
  // Blocked when Gate 1 fails: nothing published yet to acknowledge.
  // Fails when a published doc whose documentType has
  // requiresMandatoryReaders=true has zero readers assigned.
  const typeById = new Map(documentTypes.map((t) => [t.id, t]));
  let unreadDoc = null;
  let unreadUsers = [];
  let noReaderDoc = null; // mandatory-read type but zero readers assigned
  // A group target that resolves to nobody (fail-safe, §5): the document names
  // readers it cannot reach, so it is unmet — never "read by everyone".
  let deadTargetDoc = null;
  let deadTargets = [];
  // A caller that did not supply the tenant's legacy assignments cannot know
  // the reader population (DOC-B-026, design §d.1): resolving the narrower list
  // would pass a document whose only readers are in that list. Fail-safe (§5):
  // Gate 3 is not met, and says why, rather than resolving half a list.
  const readerPopulationKnown = documentAssignments !== undefined;
  for (const doc of readerPopulationKnown ? publishedDocs : []) {
    if (!deadTargetDoc) {
      const dead = unresolvedReaderTargets(doc, employees, documentAssignments);
      if (dead.length) {
        deadTargetDoc = doc;
        deadTargets = dead.map((t) => ({ id: t.id || null, type: t.type, value: t.value }));
      }
    }
    const readers = resolveReaders(doc, employees, documentAssignments);
    if (!readers.length) {
      const t = doc.documentTypeId ? typeById.get(doc.documentTypeId) : null;
      if (t && t.requiresMandatoryReaders === true && !noReaderDoc) noReaderDoc = doc;
      continue;
    }
    // Resolve acknowledgement per reader against the correct store: employee
    // readers acknowledge via the portal (employee.documentAcknowledgements);
    // legacy user readers acknowledge in-app (doc.readAcknowledgements).
    const missing = readers.filter((r) => !hasReaderAcknowledged(r, doc, employees));
    if (missing.length > 0) {
      unreadDoc = doc;
      unreadUsers = missing;
      break;
    }
  }
  let gate3State,
    gate3Reason,
    gate3Meta = null;
  if (!hasPublishedDoc) {
    gate3State = 'blocked';
    gate3Reason = 'Blocked — no published document to acknowledge';
  } else if (!readerPopulationKnown) {
    gate3State = 'fail';
    gate3Reason = 'Reader population incomplete — legacy document assignments were not supplied';
    gate3Meta = { reason: 'reader-population-incomplete' };
  } else if (unreadDoc) {
    gate3State = 'fail';
    gate3Reason = `"${unreadDoc.title}" not read by ${unreadUsers.length} user(s)`;
    gate3Meta = { missingReaders: unreadUsers, docId: unreadDoc.id, docTitle: unreadDoc.title };
  } else if (deadTargetDoc) {
    gate3State = 'fail';
    gate3Reason = `"${deadTargetDoc.title}" names ${deadTargets.length} reader group(s) that resolve to nobody (${deadTargets
      .map((t) => `${t.type}: ${t.value}`)
      .join(', ')})`;
    gate3Meta = {
      docId: deadTargetDoc.id,
      docTitle: deadTargetDoc.title,
      reason: 'reader-target-resolves-to-nobody',
      deadTargets,
    };
  } else if (noReaderDoc) {
    const tName = (typeById.get(noReaderDoc.documentTypeId) || {}).name || 'this type';
    gate3State = 'fail';
    gate3Reason = `"${noReaderDoc.title}" has no required readers assigned (${tName} requires mandatory readers)`;
    gate3Meta = {
      docId: noReaderDoc.id,
      docTitle: noReaderDoc.title,
      reason: 'no-mandatory-readers',
    };
  } else {
    gate3State = 'pass';
    gate3Reason = 'All required readers have acknowledged current version';
  }
  const gate3 = {
    id: 'read',
    label: 'All required readers acknowledged',
    pass: gate3State === 'pass',
    state: gate3State,
    reason: gate3Reason,
    meta: gate3Meta,
  };

  // GATE 4 — Domain evidence exists (approved docs, artefacts, completed risk actions)
  // controlId matching for artefacts and risks is bidirectional (legacy ↔ TCF) just
  // like the document filter above, so the list and modal agree on linkage.
  const targetCanonicalId = canonicalControlId(controlId);
  const includesControl = (ids) =>
    (ids || []).some((cid) => canonicalControlId(cid) === targetCanonicalId);
  const approvedDocs = docs.filter((d) => d.approvalDecision === 'approved' && d.approvalDecidedAt);
  // ARTF-B-002 (moved 2026-09-21): a stored blob always counts regardless of
  // URL verification state — but `filename` is no longer accepted as proof one
  // exists. See src/utils/artefactEvidence.js for why: every artefact ever
  // "uploaded" carried a filename and no stored file, so this gate passed on
  // evidence that was discarded in the browser.
  const linkedArtefacts = artefacts.filter(
    (a) => includesControl(a.controlIds) && countsAsEvidence(a)
  );
  const linkedRisks = risks.filter((r) => includesControl(riskControlIds(r)));
  const riskEvidence = linkedRisks.filter((r) =>
    (r.actions || []).some((a) => a.status === 'completed')
  );
  // Satisfied risk obligations (recurring verification work — e.g. a quarterly
  // walkthrough) count as implementation evidence for the risk's linked
  // controls while the satisfaction is inside its cadence + grace window.
  // Read-time derivation only — nothing is stored on the obligation (OBL-B-005).
  // Uses asOfMs so the forecast can age a satisfied obligation out of its
  // cadence+grace window at the projected date T (Gate 4 degradation signal).
  const nowMs = asOfMs;
  const obligationEvidence = [];
  for (const r of linkedRisks) {
    if (r.state === 'retired') continue;
    const satisfied = riskObligations.filter(
      (ob) => ob && ob.riskId === r.id && isObligationSatisfied(ob, nowMs)
    );
    if (satisfied.length > 0) obligationEvidence.push({ risk: r, obligations: satisfied });
  }
  const satisfiedObligationCount = obligationEvidence.reduce((n, e) => n + e.obligations.length, 0);
  // PDWB-03 (design §3g, §3n) — a workflow linked to this control counts only
  // when its latest judged run, attributed to this control through the run's
  // own frozen links, is countable (isRunCountable, D11 = A: never a
  // self-reviewed pass) inside its cadence window at the evaluation instant
  // (asOfMs, so the forecast ages a run out). A workflow that failed, ran
  // thin or is overdue is NAMED in the reason (WF-B-005: never shown as its
  // last good result) but does not fail a gate other evidence passes (founder
  // decision pending, see the plan). Evidence the plan withholds or that could
  // not be read is named, never silently dropped.
  const wf = workflowEvidence
    ? controlWorkflowEvidence(workflowEvidence, (cid) => canonicalControlId(cid) === targetCanonicalId, {
        now: asOfMs,
        enabledObligations,
        obligationsById,
      })
    : { status: 'unavailable', countable: [], notCounted: [] };
  const workflowCountable = wf.countable;
  const weakestWorkflowStrength = workflowCountable
    .map((w) => w.achievedStrength)
    .sort((a, b) => strengthRank(a) - strengthRank(b))[0];
  const workflowNote = [
    ...wf.notCounted.map((w) => `workflow "${w.title}" not counted — ${w.label}`),
    // Named only when the tenant had workflows (or an older server did not
    // say) — not on every control of a tenant that never had one.
    wf.status === 'not-in-plan' && wf.hadDefinitions !== false
      ? 'workflow evidence not counted — workflows are not in your plan'
      : null,
    wf.status === 'unavailable' ? 'workflow evidence not counted — it could not be read' : null,
  ].filter(Boolean);
  const hasEvidence =
    approvedDocs.length > 0 ||
    linkedArtefacts.length > 0 ||
    riskEvidence.length > 0 ||
    obligationEvidence.length > 0 ||
    workflowCountable.length > 0;

  const gate4 = {
    id: 'evidence',
    label: 'Implementation evidence',
    pass: hasEvidence,
    state: hasEvidence ? 'pass' : 'fail',
    reason: [
      !hasEvidence
        ? 'No implementation evidence (approvals, artefacts, or completed actions) for this control'
        : [
            approvedDocs.length > 0 ? `${approvedDocs.length} approved document(s)` : null,
            linkedArtefacts.length > 0 ? `${linkedArtefacts.length} artefact(s)` : null,
            riskEvidence.length > 0 ? `${riskEvidence.length} risk(s) with completed actions` : null,
            satisfiedObligationCount > 0
              ? `${satisfiedObligationCount} satisfied risk obligation(s)`
              : null,
            workflowCountable.length > 0
              ? `${workflowCountable.length} workflow(s) with a current run (${
                  workflowCountable.length > 1 ? 'weakest evidence strength' : 'evidence strength'
                } ${weakestWorkflowStrength})`
              : null,
          ]
            .filter(Boolean)
            .join(', '),
      ...workflowNote,
    ].join('; '),
    meta: {
      approvedDocs: approvedDocs.map((d) => d.id),
      artefactIds: linkedArtefacts.map((a) => a.id),
      // detailed entries for drill-down (Phase 2 will use these)
      approvedDocsDetail: approvedDocs.map((d) => ({
        id: d.id,
        title: d.title,
        version: d.version,
        approvalDecidedAt: d.approvalDecidedAt,
      })),
      artefactsDetail: linkedArtefacts.map((a) => ({
        id: a.id,
        title: a.title || a.name,
        type: a.type,
      })),
      risksWithCompletedActions: riskEvidence.map((r) => ({
        id: r.id,
        title: r.title,
        completedActions: (r.actions || [])
          .filter((a) => a.status === 'completed')
          .map((a) => ({ id: a.id, title: a.title })),
      })),
      // PDWB-03: the workflows behind the reason, countable and not.
      workflowEvidenceStatus: wf.status,
      workflowRunsDetail: workflowCountable,
      workflowsNotCounted: wf.notCounted,
      satisfiedObligationsDetail: obligationEvidence.map(({ risk, obligations: obs }) => ({
        riskId: risk.id,
        riskName: risk.name || risk.title,
        obligations: obs.map((ob) => ({
          id: ob.id,
          name: ob.name,
          lastSatisfiedAt: ob.lastSatisfiedAt,
        })),
      })),
    },
  };

  // GATE 5 — Statement of Applicability + obligations current.
  //
  // For every catalogue obligation that maps to this control (via TCF IDs):
  //   • in_scope (enabled): check fulfilment — fails if overdue.
  //   • out_of_scope (with reason): passes — explicit SoA exclusion recorded.
  //   • undecided (no entry): gate fails — ISO 27001 clause 6.1.3 requires
  //     every control to have an explicit applicability decision.
  //
  // There is no longer a migration-compat exemption. Until GH #6559 this gate
  // enumerated undecided obligations ONLY when the tenant had recorded at least
  // one decision somewhere, so a control on a tenant that had never touched the
  // feature — nothing linked, nothing excluded — satisfied both terms and
  // PASSED. A control nobody had ever considered counted as satisfied on the
  // obligations dimension, and the gate's own reason string said so to no one.
  // Zero obligations means nothing has been done, and the gate now says that.
  // The way out is a decision, not silence: link the obligation, or exclude it
  // with a clause 6.1.3 justification. Both are reachable in bulk from the
  // Obligation Coverage guide, and every TCF control maps to at least one
  // catalogue obligation, so no control can be left in a state its tenant
  // cannot clear.
  //
  // Obligation scfIds are already in TCF format; canonicalControlId maps legacy
  // controlIds → TCF, so both sides are in the same namespace for comparison.
  const catalogueForControlRaw = OBLIGATION_CATALOG.filter((def) =>
    (def.scfIds || []).some((sid) => canonicalControlId(sid) === targetCanonicalId)
  );

  const linkedObligations = obligations.filter((o) => includesControl(o.controlIds));
  const overdueObligations = linkedObligations.filter((o) => o.status === 'overdue');
  const fulfilledObligations = linkedObligations.filter((o) => o.status === 'fulfilled');
  // #6608 — an obligation the engine could not assess because this tenant's
  // plan withheld the data it reads. It is neither fulfilled (it asserts
  // nothing) nor "in progress" (there is no work the tenant can do), so it is
  // counted separately rather than folded into either. It cannot be overdue —
  // obligationEvaluator nulls the due date for exactly this reason.
  const notEntitledObligations = linkedObligations.filter((o) => o.status === 'not-entitled');
  const inProgressObligations = linkedObligations.filter(
    (o) => o.status !== 'overdue' && o.status !== 'fulfilled' && o.status !== 'not-entitled'
  );

  // SoA decision check. `hasAnyDecisions` no longer gates whether the check
  // runs (see above) — it survives only to describe the tenant's starting
  // point in gate meta, for surfaces that distinguish "never begun" from
  // "part-way through".
  const hasAnyDecisions = Object.keys(obligationDecisions).length > 0;
  const enabledIds = new Set(linkedObligations.map((o) => o.obligationId));

  // CRA-B-001: an obligation whose only linked frameworks are not enabled for
  // this tenant (e.g. fw-cra pre-opt-in) must not count toward this gate —
  // mirrors ObligationCatalog.jsx's fix for the same catalogue walk. An
  // obligation the tenant has already enabled or explicitly decided on stays
  // in scope regardless: disabling a framework doesn't cascade-disable those.
  const enabledFrameworkIds = enabledFrameworkIdSet(frameworks);
  const catalogueForControl =
    frameworks === undefined
      ? catalogueForControlRaw
      : catalogueForControlRaw.filter((def) => {
          if (enabledIds.has(def.id)) return true;
          if (obligationDecisions[def.id]?.decision) return true;
          const fwIds = def.frameworkIds || [];
          if (fwIds.length === 0) return true;
          return fwIds.some((id) => enabledFrameworkIds.has(id));
        });

  // Catalogue obligations for this control that are not enabled (not in scope).
  const notEnabledCatalogue = catalogueForControl.filter((def) => !enabledIds.has(def.id));

  const outOfScopeObligations = [];
  const undecidedObligations = [];
  for (const def of notEnabledCatalogue) {
    const dec = obligationDecisions[def.id];
    if (dec?.decision === 'out_of_scope' && dec.reason) {
      outOfScopeObligations.push({ obligationId: def.id, title: def.title, reason: dec.reason });
    } else {
      undecidedObligations.push({ obligationId: def.id, title: def.title });
    }
  }

  // #6608 — a not-entitled obligation FAILS this gate; it does not merely get
  // counted beside the others. Two reasons, and both are §5.
  //
  // The gate's own claim is "obligations current". An obligation the engine
  // could not assess, because the tenant's plan withheld the records it is
  // judged on, does not support that claim — letting the gate pass on one is
  // the same over-claim this whole change exists to stop, one level up. The
  // obligation layer refuses to count such a criterion as fulfilment; the
  // control layer must refuse to count it as evidence.
  //
  // And without this, the change would move a verdict FAVOURABLY on at least
  // one surface. ThreatToRiskModal passes evaluateObligations() (unfulfilled
  // only) into this gate, where an obligation absent from the list falls
  // through to `undecidedObligations` and fails. So for a gated tenant a
  // vacuously-FULFILLED obligation used to be absent and fail the gate, while
  // a not-entitled one is present and would have passed it — raising that
  // screen's mitigation factor and lowering the residual risk it shows. A fix
  // for an over-claim must not buy a second one on the way past.
  //
  // (That surface's use of evaluateObligations() rather than
  // evaluateAllObligations() was a pre-existing defect of its own — the latter
  // exists precisely so this gate can see fulfilled obligations — and it
  // understated compliance for every tenant holding one. It got the decision
  // this comment said it deserved: GH #6706, founder decision A. Fixed. The
  // understatement was display-only — that screen computes a residualScore for
  // the reader and does not persist it — so nothing stored needed re-deriving.
  // The paragraph above still describes why a not-entitled obligation must not
  // pass this gate; it no longer describes a live understatement.)
  const gate5pass =
    overdueObligations.length === 0 &&
    undecidedObligations.length === 0 &&
    notEntitledObligations.length === 0;

  let gate5Reason;
  if (linkedObligations.length === 0 && outOfScopeObligations.length === 0) {
    gate5Reason = `No obligations linked to this control — ${undecidedObligations.length} need a Statement of Applicability decision`;
  } else if (undecidedObligations.length > 0) {
    gate5Reason = `${undecidedObligations.length} obligation${undecidedObligations.length > 1 ? 's' : ''} need a Statement of Applicability decision`;
  } else if (notEntitledObligations.length > 0 || overdueObligations.length > 0) {
    // BOTH are named when both are present, and not-entitled comes first.
    //
    // An earlier version branched on not-entitled alone and dropped the overdue
    // titles from the string entirely. The gate failed either way, so nothing
    // was over-claimed — but the tenant lost the more actionable half, which is
    // the wrong thing to lose. Not-entitled leads because it is not something
    // they have failed to do and the sentence must not open as a missed
    // deadline; overdue follows because it is the part they can act on.
    const parts = [];
    if (notEntitledObligations.length > 0) {
      parts.push(
        `${notEntitledObligations.length} obligation${notEntitledObligations.length > 1 ? 's' : ''} cannot be assessed on this plan: ${notEntitledObligations.map((o) => o.title).join(', ')}`
      );
    }
    if (overdueObligations.length > 0) {
      parts.push(
        `${overdueObligations.length} overdue: ${overdueObligations.map((o) => o.title).join(', ')}`
      );
    }
    gate5Reason = parts.join('; ');
  } else if (linkedObligations.length === 0 && outOfScopeObligations.length > 0) {
    gate5Reason = `All ${outOfScopeObligations.length} obligation${outOfScopeObligations.length > 1 ? 's' : ''} marked out of scope`;
  } else {
    const parts = [];
    if (fulfilledObligations.length) parts.push(`${fulfilledObligations.length} fulfilled`);
    if (inProgressObligations.length) parts.push(`${inProgressObligations.length} in progress`);
    // No `not in plan` part here: this branch only runs when the gate PASSED,
    // and a not-entitled obligation now fails it. Kept in gate meta counts.
    if (outOfScopeObligations.length) parts.push(`${outOfScopeObligations.length} out of scope`);
    parts.push('0 overdue');
    gate5Reason = parts.join(', ');
  }

  const gate5 = {
    id: 'obligations',
    label: 'Obligations current',
    pass: gate5pass,
    state: gate5pass ? 'pass' : 'fail',
    reason: gate5Reason,
    counts: {
      total: linkedObligations.length,
      fulfilled: fulfilledObligations.length,
      inProgress: inProgressObligations.length,
      notEntitled: notEntitledObligations.length,
      overdue: overdueObligations.length,
      outOfScope: outOfScopeObligations.length,
      undecided: undecidedObligations.length,
    },
    meta: {
      // Nothing recorded against THIS control — neither linked nor excluded.
      // Previously this also required the whole tenant to have made no decision
      // anywhere, which made the flag flip off across every control the moment
      // one unrelated obligation was decided.
      vacuous: linkedObligations.length === 0 && outOfScopeObligations.length === 0,
      // Has this tenant begun recording applicability decisions at all?
      tenantHasDecisions: hasAnyDecisions,
      linkedObligations: linkedObligations.map((o) => ({
        obligationId: o.obligationId,
        title: o.title,
        dueDate: o.dueDate,
        status: o.status,
      })),
      overdueObligations: overdueObligations.map((o) => ({
        obligationId: o.obligationId,
        title: o.title,
        dueDate: o.dueDate,
      })),
      outOfScopeObligations,
      undecidedObligations,
    },
  };

  // GATE 6 — Control has an assigned owner (ISO 27001 A.5.1 — explicit delegation).
  // Only active when ownershipSettings.gateEnabled is true. When disabled the gate
  // is omitted entirely so the gate count stays at 5 and no existing scores shift.
  //
  // KNOWN SHORTFALL, stated here rather than left implicit (COP-B-004).
  // `!!ownerId` is weaker than what the rest of the product now means by "has an
  // owner". `src/utils/controlOwnerResolution.js` resolves an ownerId to a real,
  // currently-employed person and REFUSES an ambiguous one; `ownershipGaps.js`
  // and the control-owner portal both read that resolution, so a control owned
  // by somebody who has left, or by an address two employee records share,
  // renders as UNASSIGNED there. This gate still passes for all of those.
  //
  // It is deliberate, not overlooked. GH #6959 settled it: the injury was never
  // this boolean, it was that NOBODY WAS TOLD — a control whose owner walked out
  // last month looked exactly like one whose owner is at their desk. So the fix
  // shipped as visibility (a tag on the control on every surface that renders
  // one, plus an admin entry in the Action Centre) rather than as a score move,
  // and this gate stays as it is. A tenant with it enabled now sees "Control
  // owner assigned" pass beside a tag reading "Owner has left" — both facts on
  // screen, which is honest, where before there was only the first.
  //
  // Flipping the gate remains open and is §5-safe in direction (it can only turn
  // a pass into a fail), but it drops live tenants' scores on deploy, so it is a
  // separate announced change rather than a side effect of a visibility fix.
  const gate6Gates = [];
  if (ownershipSettings.gateEnabled) {
    const scfId = canonicalControlId(controlId);
    const record = controlOwnership[controlId] || controlOwnership[scfId] || null;
    const ownerId = record?.ownerId || null;
    const gate6pass = !!ownerId;
    gate6Gates.push({
      id: 'ownership',
      label: 'Control owner assigned',
      pass: gate6pass,
      state: gate6pass ? 'pass' : 'fail',
      reason: gate6pass
        ? `Owner assigned (ISO 27001 A.5.1)`
        : 'No owner assigned — explicit delegation required by ISO 27001 A.5.1',
      meta: { ownerId: ownerId || null },
    });
  }

  const gates = [gate1, gate2, gate3, gate4, gate5, ...gate6Gates];
  const passCount = gates.filter((g) => g.pass).length;
  const totalGates = gates.length;

  let status;
  if (!gate5.pass && overdueObligations.length > 0) status = 'non-compliant';
  else if (passCount === totalGates) status = 'compliant';
  else if (passCount >= Math.ceil(totalGates * 0.6)) status = 'partial';
  else if (passCount === 0) status = 'non-compliant';
  else status = 'non-compliant';

  // SCF-AIG controls are evidence-first: if the evidence gate passes but the
  // document gate does not, count as partial rather than non-compliant.
  // This reflects that AI governance maturity is demonstrated through practices
  // and records (gate 4) even when a formal policy document is not yet published.
  if (status === 'non-compliant' && controlId.startsWith('SCF-AIG-') && gate4.pass) {
    status = 'partial';
  }

  return { status, gates, docs };
}

function getControlStatus(controlId, documents, domainContext, asOf) {
  return evaluateControl(controlId, documents, domainContext, asOf).status;
}

module.exports = {
  evaluateControl,
  getControlStatus,
};
